/**
 * NumericField — un monto, con su etiqueta, su prefijo y su error.
 *
 * ─── 🔴 Usa el `NumericInput` del repo, NO el input del sistema ───────────────
 *
 * Es el mismo caso que el `DateField`, y por la misma razón: el control del
 * sistema sería una **regresión medible**. Su parser hace
 *
 *     value.replace(/[^\d,-]/g, '').replace(',', '.')
 *
 * o sea asume que el separador decimal es la coma y que el de miles es el
 * punto. Eso es cierto en Chile, Argentina y Colombia, y **falso en Perú y en
 * México**, donde es al revés. Medido:
 *
 *     es-CL   "1.234,56"  →  1234.56    ✅
 *     es-PE   "1,234.56"  →  1.23456    🔴 mil veces menos
 *     es-MX   "1,234.56"  →  1.23456    🔴
 *
 * Clobi ya tiene un tenant peruano, así que no es hipotético. Y es la misma
 * clase de defecto que la orden de compra que imprimía «30» donde eran 29,75:
 * un número que sale plausible y equivocado, sin ningún error.
 *
 * `NumericInput` resuelve las tres cosas que el del sistema no: lee los
 * separadores del locale (`getDecimalSeparator` / `getThousandsSeparator`),
 * **saca los separadores de miles mientras el campo está enfocado** para que se
 * pueda escribir, y trunca a los decimales configurados al salir, de modo que
 * lo guardado coincida con lo que se muestra.
 *
 * Lo que sí se adopta del sistema es la **capa de campo**: etiqueta asociada,
 * `aria-invalid`, `aria-describedby`, el prefijo y el sufijo dentro del
 * recuadro, y el piso de ancho de `--field-min-w-numeric`.
 *
 * ⚠️ Ese piso no es cosmético: `.cl-input--numeric` alinea a la derecha, así
 * que cuando el valor no cabe el navegador **oculta el comienzo del número sin
 * ninguna señal** y `$1.284.593.000` se lee como `593.000`.
 *
 * ⚠️ `onChange` entrega un **string numérico** (`'1234.56'`), no un `number`,
 * que es lo que `react-hook-form` y el resto del producto ya consumen. El del
 * sistema entrega `number` y perdería la precisión de un decimal al vuelo.
 */
import { useId } from 'react'
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

import { NumericInput } from '../NumericInput'

export interface NumericFieldProps
  extends Omit<
    HTMLAttributes<HTMLDivElement>,
    // `prefix` existe como atributo HTML (RDFa) y ahí es un string; acá es un nodo.
    'className' | 'style' | 'onChange' | 'prefix'
  > {
  label?: ReactNode
  value?: string | number | null
  /** Recibe un string numérico puro: `'1234.56'`. */
  onChange?: (value: string) => void
  onBlur?: () => void
  /** Por defecto el del tenant. Decide los separadores, no solo la apariencia. */
  locale?: string
  decimals?: number
  prefix?: ReactNode
  suffix?: ReactNode
  error?: ReactNode
  hint?: ReactNode
  disabled?: boolean
  readOnly?: boolean
  size?: 'sm' | 'md'
  id?: string
  name?: string
  placeholder?: string
  className?: string
  style?: CSSProperties
}

export function NumericField({
  label,
  value,
  onChange,
  onBlur,
  locale = 'es-CL',
  decimals = 0,
  prefix,
  suffix,
  error,
  hint,
  disabled = false,
  readOnly = false,
  size = 'md',
  id: idProp,
  name,
  placeholder,
  className = '',
  style,
  ...rest
}: NumericFieldProps) {
  const auto = useId()
  const id = idProp || `cl-${auto}`
  const idError = `${id}-err`
  const idAyuda = `${id}-hint`

  const afijo: CSSProperties = {
    color: 'rgb(var(--color-content-subtle))',
    fontSize: 12.5,
    flex: 'none',
  }

  return (
    <div className={`cl-field ${className}`.trim()} style={style} {...rest}>
      {label && (
        <label className="cl-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div
        className="cl-inputwrap"
        style={{
          minWidth: 'var(--field-min-w-numeric)',
          ...(size === 'sm' ? { height: 'var(--control-h-xs)' } : null),
        }}
      >
        {prefix && <span style={afijo}>{prefix}</span>}
        <NumericInput
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          locale={locale}
          decimals={decimals}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          // `cl-num` es mono + tabular + alineado a la derecha, que es lo que
          // el sistema arma con `cl-mono` más un `textAlign` en línea.
          className="cl-num"
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error ? idError : null, hint ? idAyuda : null].filter(Boolean).join(' ') ||
            undefined
          }
        />
        {suffix && <span style={afijo}>{suffix}</span>}
      </div>
      {error && (
        <div className="cl-error" id={idError} role="alert">
          {error}
        </div>
      )}
      {hint && !error && (
        <div className="cl-hint" id={idAyuda}>
          {hint}
        </div>
      )}
    </div>
  )
}
